/**
 * Vorschau für Artikel- und Designeditor: `@pat/design-html` rendert im Browser dasselbe HTML, das der
 * Worker senden würde; angezeigt in einem `iframe` mit `sandbox` ohne Skripte, in den Breiten des Altsystems (App 3). Hier
 * stehen auch die deutschen Texte der Renderwarnungen, Sperrgründe und Hinweise, die beide Editoren anzeigen.
 */
import { Alert, Badge, Button, Card, Group, Modal, SegmentedControl, Stack, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { has, t } from '../texts.js';

export type RenderOutcome = { html: string; warnings: string[] } | { html: string; warnings: string[]; error: string };

/** Warnungscode des Renderers, Sperrgrund oder Hinweis vor dem Veröffentlichen → deutscher Text; Unbekanntes bleibt als Code lesbar. */
export function warningText(code: string): string {
  const base = code.split(':')[0] ?? code;
  for (const key of [`render.warning.${base}`, `designs.blocker.${base}`, `designs.hint.${base}`]) if (has(key)) return t(key);
  return base;
}

/**
 * Ansichtsbreiten wie im Altsystem (App 3, Flori 30.10.2026): Desktop 1260, Laptop 988, Tablet 768, Mobil 480 px. Die
 * Design-CSS baut auf diesen Werten auf; ist der Bereich schmaler, scrollt die Vorschau waagerecht statt umzubrechen.
 */
export const PREVIEW_WIDTHS = { desktop: 1260, laptop: 988, tablet: 768, mobile: 480 } as const;
export type PreviewWidth = keyof typeof PREVIEW_WIDTHS;

/**
 * Zwei Verhaltensgruppen je verfügbarer Breite (Flori 30.10.2026): passt neben die Desktop-Vorschau (1260 px plus Rahmen)
 * auch der Editor (Navigation 200, Ränder, Editor mindestens ~640 px), stehen beide nebeneinander, die Vorschau rechts
 * mitlaufend in voller Fensterhöhe. Sonst gehört der Platz dem Editor, und die Vorschau öffnet als Vollbild-Dialog über
 * den Button „Vorschau“ in der Aktionsleiste (`PreviewButton`). Der Dialog bleibt eingehängt: die Vorschau rechnet beim
 * Bearbeiten weiter und ist beim Öffnen sofort aktuell.
 */
export const SIDE_BY_SIDE_QUERY = '(min-width: 2200px)';
/** Kopfzeile der App (60 px) plus Innenabstand oben und unten (je 16 px). */
const FRAME_OFFSET = 'calc(60px + 2 * var(--mantine-spacing-md))';

const PreviewMode = createContext<{ dialog: boolean; open: () => void } | undefined>(undefined);
/** Höhe der Vorschau füllt den Rahmen (nebeneinander und im Dialog); ohne Rahmen feste Höhe. */
const PreviewFill = createContext<{ fill: boolean; heading: boolean }>({ fill: false, heading: true });

export function EditorLayout({ children, preview }: { children: ReactNode; preview: ReactNode }) {
  // Sofort aus `matchMedia` lesen: sonst stünde auf breiten Schirmen erst kurz der Dialog-Modus.
  const wide = useMediaQuery(SIDE_BY_SIDE_QUERY, undefined, { getInitialValueInEffect: false }) === true;
  const [opened, setOpened] = useState(false);
  // Wird das Fenster breit genug, steht die Vorschau ohnehin daneben.
  useEffect(() => {
    if (wide) setOpened(false);
  }, [wide]);
  return (
    <PreviewMode.Provider value={{ dialog: !wide, open: () => setOpened(true) }}>
      {wide ? (
        <div
          data-layout="side-by-side"
          style={{ display: 'grid', gridTemplateColumns: `minmax(0, 1fr) minmax(0, ${PREVIEW_WIDTHS.desktop + 40}px)`, gap: 'var(--mantine-spacing-md)', alignItems: 'start' }}
        >
          {children}
          <div style={{ position: 'sticky', top: 'calc(60px + var(--mantine-spacing-md))', height: `calc(100dvh - ${FRAME_OFFSET})` }}>
            <PreviewFill.Provider value={{ fill: true, heading: true }}>{preview}</PreviewFill.Provider>
          </div>
        </div>
      ) : (
        <div data-layout="dialog">
          {children}
          <Modal opened={opened} onClose={() => setOpened(false)} title={t('preview.title')} fullScreen keepMounted>
            <div style={{ height: 'calc(100dvh - 90px)' }}>
              <PreviewFill.Provider value={{ fill: true, heading: false }}>{preview}</PreviewFill.Provider>
            </div>
          </Modal>
        </div>
      )}
    </PreviewMode.Provider>
  );
}

/** „Vorschau“ für die Aktionsleiste; nur sichtbar, wenn die Vorschau nicht daneben steht. */
export function PreviewButton() {
  const mode = useContext(PreviewMode);
  if (!mode?.dialog) return null;
  return (
    <Button variant="default" onClick={mode.open}>
      {t('preview.open')}
    </Button>
  );
}

export function Preview({ result, placeholder, children }: {
  result: RenderOutcome | undefined;
  /** Statt der Vorschau anzeigen (z. B. kein Design gewählt oder es lädt noch); dann ohne `iframe`. */
  placeholder?: ReactNode;
  /** Hinweise zwischen Warnungen und Vorschau. */
  children?: ReactNode;
}) {
  const [width, setWidth] = useState<PreviewWidth>('desktop');
  const { fill, heading } = useContext(PreviewFill);
  return (
    <Card withBorder padding="sm" style={fill ? { height: '100%', display: 'flex', flexDirection: 'column' } : undefined}>
      <Stack gap="xs" style={fill ? { flex: 1, minHeight: 0 } : undefined}>
        <Group justify={heading ? 'space-between' : 'flex-end'}>
          {heading && <Title order={4}>{t('preview.title')}</Title>}
          <SegmentedControl
            size="xs"
            value={width}
            onChange={v => setWidth(v as PreviewWidth)}
            data={(Object.keys(PREVIEW_WIDTHS) as PreviewWidth[]).map(value => ({ value, label: t(`preview.${value}`) }))}
          />
        </Group>
        {placeholder ??
          (result && 'error' in result ? (
            <Alert color="red">{t('preview.failed')}</Alert>
          ) : (
            <>
              {result && result.warnings.length > 0 && (
                <Group gap={4}>
                  {[...new Set(result.warnings.map(warningText))].map(text => (
                    <Badge key={text} color="yellow" variant="light">
                      {text}
                    </Badge>
                  ))}
                </Group>
              )}
              {children}
              <div style={fill ? { overflowX: 'auto', flex: 1, minHeight: 320 } : { overflowX: 'auto' }}>
                <iframe
                  title={t('preview.title')}
                  sandbox=""
                  srcDoc={result?.html ?? ''}
                  style={{ display: 'block', width: PREVIEW_WIDTHS[width], maxWidth: 'none', height: fill ? '100%' : 640, border: '1px solid var(--mantine-color-gray-3)', background: '#fff' }}
                />
              </div>
            </>
          ))}
      </Stack>
    </Card>
  );
}
